import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, ErrorAlert } from '../components/ui';
import * as publicApi from '../api/public';
import { ui, cn } from '../lib/ui';

export default function PublicEventDetail() {
  const { orgSlug, eventId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await publicApi.getOrgEventDetail(orgSlug, eventId);
      if (id !== runIdRef.current) return;
      setData(result);
    } catch (err) {
      if (id !== runIdRef.current) return;
      setError(err.message || 'Event not found');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, [orgSlug, eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={ui.publicPage}>
        <Spinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={ui.publicPage}>
        <div className={ui.publicContainer}>
          <ErrorAlert message={error || 'Event not found'} />
          <p className="mt-4">
            <Link to={`/o/${orgSlug}/events`}>Back to events</Link>
          </p>
        </div>
      </div>
    );
  }

  const { event, orgName, requiresLogin, detailsHidden } = data;

  return (
    <div className={ui.publicPage}>
      <header className={ui.publicHeader}>
        <div className={ui.publicContainer}>
          <div className="flex items-center gap-3">
            <Link
              to={`/o/${orgSlug}`}
              className="font-semibold text-inherit no-underline hover:no-underline"
            >
              {orgName}
            </Link>
            <span className={ui.muted}>/</span>
            <Link
              to={`/o/${orgSlug}/events`}
              className="text-dcon-link no-underline"
            >
              Events
            </Link>
          </div>
        </div>
      </header>

      <main className={cn(ui.publicContainer, 'pt-8')}>
        <article className={cn(ui.card, 'max-w-3xl')}>
          <h1 className="mt-0 mb-3 text-title-l">
            {event.name}
          </h1>

          <div className="mb-4 flex flex-wrap gap-4 text-body-s text-dcon-ink-body">
            {event.startsAt && (
              <div>
                <strong>Date</strong>
                <br />
                {new Date(event.startsAt).toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
            )}
            {event.startsAt && (
              <div>
                <strong>Time</strong>
                <br />
                {new Date(event.startsAt).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                {event.endsAt && (
                  <>
                    {' - '}
                    {new Date(event.endsAt).toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </>
                )}
              </div>
            )}
            {event.venue && (
              <div>
                <strong>Venue</strong>
                <br />
                {event.venue}
              </div>
            )}
          </div>

          {event.description && (
            <p className="leading-relaxed text-dcon-ink-body">
              {event.description}
            </p>
          )}

          {requiresLogin && (
            <div
              className={cn(ui.alertInfo, 'mt-4 flex flex-wrap items-center justify-between gap-3')}
            >
              <span>
                {detailsHidden
                  ? 'Sign in to see the full details and register for this event.'
                  : 'Sign in to register for this event.'}
              </span>
              <div className="flex gap-2">
                <Link to={`/o/${orgSlug}/login`} className={ui.btnPrimary}>
                  Sign in
                </Link>
                <Link to={`/o/${orgSlug}/register`} className={ui.btnSecondary}>
                  Join
                </Link>
              </div>
            </div>
          )}
        </article>
      </main>
    </div>
  );
}
